import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Generic CMS list + edit pane.
 *
 * Props:
 *  - entityKey: string (e.g. "courses")
 *  - publicPath: string (e.g. "/courses")     -> GET endpoint
 *  - adminPath:  string (e.g. "/admin/courses") -> POST/PUT/DELETE base
 *  - title: string
 *  - renderRow: (item) => ReactNode (compact)
 *  - emptyItem: () => object (defaults for "new")
 *  - renderForm: ({ value, onChange, branches }) => ReactNode
 *  - branches: array (optional, for course prices form)
 *  - itemLabel: string ("curso", "sucursal"…)
 *  - showSubmitButtonInForm?: boolean
 */
export default function CMSPanel({
  entityKey,
  publicPath,
  adminPath,
  title,
  renderRow,
  emptyItem,
  renderForm,
  branches,
  itemLabel,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { mode: 'create' | 'edit', value, originalId }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(publicPath);
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Error al cargar ${title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityKey]);

  const openCreate = () =>
    setEditing({ mode: "create", value: emptyItem(), originalId: null });
  const openEdit = (it) =>
    setEditing({ mode: "edit", value: JSON.parse(JSON.stringify(it)), originalId: it.id });

  const onSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = editing.value;
      if (editing.mode === "create") {
        await api.post(adminPath, payload);
        toast.success(`${itemLabel} creado`);
      } else {
        await api.put(`${adminPath}/${editing.originalId}`, payload);
        toast.success(`${itemLabel} actualizado`);
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (it) => {
    if (!window.confirm(`¿Eliminar este ${itemLabel.toLowerCase()}?`)) return;
    try {
      await api.delete(`${adminPath}/${it.id}`);
      toast.success(`${itemLabel} eliminado`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  return (
    <div data-testid={`cms-panel-${entityKey}`} className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display font-semibold tracking-tighter text-2xl md:text-3xl">
            {title}
          </h2>
          <p className="mt-1 text-sm text-[#525252]">
            {items.length} {items.length === 1 ? "elemento" : "elementos"} ·
            Estos datos se muestran en el sitio público.
          </p>
        </div>
        <button
          data-testid={`cms-${entityKey}-create`}
          onClick={openCreate}
          className="btn-primary text-[0.7rem] px-4 py-3"
        >
          <Plus size={14} strokeWidth={1.8} /> Nuevo
        </button>
      </div>

      <div className="bg-white border border-black/8">
        {loading ? (
          <div className="p-10 text-center text-[#525252]">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[#525252]">
            Aún no hay elementos. Crea el primero.
          </div>
        ) : (
          <ul className="divide-y divide-black/8" data-testid={`cms-${entityKey}-list`}>
            {items.map((it) => (
              <li
                key={it.id}
                data-testid={`cms-${entityKey}-row-${it.id}`}
                className="flex items-center gap-4 px-5 py-4 hover:bg-[#fafafa] transition-colors"
              >
                <div className="flex-1 min-w-0">{renderRow(it)}</div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    data-testid={`cms-${entityKey}-edit-${it.id}`}
                    onClick={() => openEdit(it)}
                    aria-label="Editar"
                    className="w-9 h-9 flex items-center justify-center border border-black/15 text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
                  >
                    <Pencil size={14} strokeWidth={1.5} />
                  </button>
                  <button
                    data-testid={`cms-${entityKey}-delete-${it.id}`}
                    onClick={() => onDelete(it)}
                    aria-label="Eliminar"
                    className="w-9 h-9 flex items-center justify-center border border-black/15 text-[#525252] hover:bg-[#c40000] hover:text-white hover:border-[#c40000] transition-colors"
                  >
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <EditDrawer
          title={
            editing.mode === "create"
              ? `Nuevo ${itemLabel.toLowerCase()}`
              : `Editar ${itemLabel.toLowerCase()}`
          }
          onClose={() => setEditing(null)}
          onSave={onSave}
          saving={saving}
          entityKey={entityKey}
        >
          {renderForm({
            value: editing.value,
            onChange: (patch) =>
              setEditing((e) => ({ ...e, value: { ...e.value, ...patch } })),
            branches: branches || [],
            mode: editing.mode,
          })}
        </EditDrawer>
      )}
    </div>
  );
}

function EditDrawer({ title, onClose, onSave, saving, children, entityKey }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, saving]);

  return (
    <div className="fixed inset-0 z-[120] flex" data-testid={`cms-${entityKey}-drawer`}>
      <button
        onClick={onClose}
        aria-label="Cerrar"
        className="absolute inset-0 bg-black/55 cursor-default"
      />
      <div className="relative ml-auto w-full md:max-w-[640px] h-full bg-white flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/8 bg-white sticky top-0 z-10">
          <h3 className="font-display font-semibold text-lg tracking-tight">{title}</h3>
          <button
            data-testid={`cms-${entityKey}-drawer-close`}
            onClick={onClose}
            aria-label="Cerrar"
            className="w-9 h-9 flex items-center justify-center border border-black/15 text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
          >
            <X size={16} strokeWidth={1.6} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 md:p-7 space-y-5">{children}</div>
        <div className="px-5 pt-4 pb-20 md:pb-4 border-t border-black/8 bg-white flex justify-end gap-3">
          <button
            data-testid={`cms-${entityKey}-drawer-cancel`}
            onClick={onClose}
            disabled={saving}
            className="text-xs uppercase tracking-[0.18em] font-semibold px-4 py-3 border border-black/15 hover:bg-[#0a0a0a] hover:text-white transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            data-testid={`cms-${entityKey}-drawer-save`}
            onClick={onSave}
            disabled={saving}
            className="btn-primary text-[0.7rem] px-4 py-3 disabled:opacity-60"
          >
            {saving ? (
              <>
                <Loader2 size={14} strokeWidth={1.8} className="animate-spin" /> Guardando
              </>
            ) : (
              <>
                <Save size={14} strokeWidth={1.8} /> Guardar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
